import { useMemo, useState, useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Deal, DealStatus, TimeFilter, Product, DealProduct } from "../types";
import { StatCard, StatusPill, SortHeader, Sort, currency, Card, Button, PhoneInput } from "@repo/o37-ui";
import DealModal from "./DealModal";
import { useToast } from "@repo/o37-ui";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function PartnerDashboard({ partnerId }: { partnerId: string }) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editing, setEditing] = useState<Deal | null>(null);
  const [sort, setSort] = useState<Sort>({ key: "createdAt", dir: "desc" });
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [mercuryBalance, setMercuryBalance] = useState<number | null>(null);
  const [showIntakeForm, setShowIntakeForm] = useState(false);
  const [intakeProducts, setIntakeProducts] = useState<DealProduct[]>([]);
  const [intakePhone, setIntakePhone] = useState("");
  const { showToast } = useToast();

  // Load deals and products
  useEffect(() => {
    if (!partnerId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    Promise.all([
      loadDeals(partnerId),
      loadMercuryBalance(partnerId),
      loadProducts()
    ]).finally(() => setLoading(false));
  }, [partnerId]);

  const openDeal = async (dealId: string) => {
    try {
      setOpeningId(dealId);
      const response = await fetch(`${API_URL}/deals/${dealId}`);
      if (response.ok) {
        const deal = await response.json();
        setEditing(deal);
      } else {
        showToast("Failed to load deal details", "error");
      }
    } catch (error) {
      console.error("Failed to load deal:", error);
      showToast("Failed to load deal details", "error");
    } finally {
      setOpeningId(null);
    }
  };

  const loadDeals = async (id: string) => {
    try {
      const response = await fetch(`${API_URL}/deals/partner/${id}`);
      if (response.ok) {
        const data = await response.json();
        setDeals(data);
      }
    } catch (error) {
      console.error("Failed to load deals:", error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Failed to load products:", error);
    }
  };

  const loadMercuryBalance = async (id: string) => {
    try {
      const response = await fetch(`${API_URL}/mercury/partner/${id}/balance`);
      if (response.ok) {
        const data = await response.json();
        setMercuryBalance(data.balance);
      }
    } catch (error) {
      console.error("Failed to load Mercury balance:", error);
    }
  };

  const getAnnualValue = (dealValue: number, billingCycle?: string) => {
    if (!billingCycle) return dealValue;
    switch (billingCycle.toLowerCase()) {
      case "monthly": return dealValue * 12;
      case "quarterly": return dealValue * 4;
      case "annually": return dealValue;
      case "one_time": return dealValue;
      default: return dealValue;
    }
  };

  const isStale = (deal: Deal) => {
    // If status is closed or paid, it's not "stale" in a bad way
    if (deal.status === 'closed' || deal.status === 'paid') return false;
    
    const lastActivity = deal.updatedAt ? new Date(deal.updatedAt) : new Date(deal.createdAt);
    const diffTime = Math.abs(new Date().getTime() - lastActivity.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    return diffDays > 30;
  };

  // Filter and Sort
  const filteredDeals = useMemo(() => {
    if (timeFilter === "all") return deals;
    const now = new Date();
    const days = timeFilter === "30days" ? 30 : 90;
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return deals.filter(d => new Date(d.createdAt) >= cutoff);
  }, [deals, timeFilter]);

  const sorted = useMemo(() => {
    const mult = sort.dir === "asc" ? 1 : -1;
    const cpy = [...filteredDeals];
    cpy.sort((a, b) => {
      const A: any = a[sort.key as keyof Deal];
      const B: any = b[sort.key as keyof Deal];
      if (sort.key === "name" || sort.key === "status" || sort.key === "createdAt") {
        return (A?.toString() || "").localeCompare(B?.toString() || "") * mult;
      }
      return ((A as number) - (B as number)) * mult;
    });
    return cpy;
  }, [filteredDeals, sort]);

  const groupedDeals = useMemo(() => {
    const groups: Record<DealStatus, Deal[]> = {
      qualified: [], "pre-negotiation": [], negotiation: [], closed: [], paid: [], lost: [],
    };
    sorted.forEach(deal => {
      if (groups[deal.status]) groups[deal.status].push(deal);
    });
    return groups;
  }, [sorted]);

  const unpaidCommission = filteredDeals
    .filter((d) => d.status === "closed")
    .reduce((sum, d) => sum + d.commissionValue, 0);

  const totalPaidCommission = filteredDeals
    .filter((d) => d.status === "paid")
    .reduce((sum, d) => sum + d.commissionValue, 0);

  const openPipelineValue = filteredDeals
    .filter((d) => ["qualified", "pre-negotiation", "negotiation"].includes(d.status))
    .reduce((sum, d) => sum + getAnnualValue(d.dealValue, d.billingCycle), 0);

  const openCommissionValue = filteredDeals
    .filter((d) => ["qualified", "pre-negotiation", "negotiation"].includes(d.status))
    .reduce((sum, d) => sum + getAnnualValue(d.commissionValue, d.billingCycle), 0);

  // Helper to add product to editing deal - REMOVED (logic moved to inline onChange handler)
  /*
  const addProductToDeal = (productId: string) => {
    if (!editing) return;
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const newDealProduct: DealProduct = {
      dealId: editing.id,
      productId: product.id,
      quantity: 1,
      priceAtDeal: product.price,
      product: product
    };

    setEditing({
      ...editing,
      products: [...(editing.products || []), newDealProduct]
    });
  };
  */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-neutral-600">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {/* Resources & Workflows */}
      <section className="px-6 sm:px-12 mt-10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-[13px] uppercase tracking-wider text-neutral-500 mb-3">Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Docs & Assets */}
            <Card className="p-6">
              <h3 className="text-[11px] uppercase tracking-wider text-neutral-500 mb-4">Docs & Assets</h3>
              <div className="flex gap-3">
                <a href="https://docs.google.com/document/d/1uILDd_PbJ0Jv6xfaLnJvtKCKKXjLNY1cwKVvg8eag8Q/edit?tab=t.0#heading=h.o1126mz6r04u" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-neutral-200 rounded-xl px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors">
                  <span>Partner Playbook</span>
                  <span className="text-neutral-400">↗</span>
                </a>
                <a href="https://drive.google.com/drive/folders/18onfGyRySz313Fmx-yT1iuI6MdavApQQ" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-neutral-200 rounded-xl px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors">
                  <span>Brand Assets</span>
                  <span className="text-neutral-400">↗</span>
                </a>
              </div>
            </Card>

            {/* Workflows */}
            <Card className="p-6">
              <h3 className="text-[11px] uppercase tracking-wider text-neutral-500 mb-4">Workflows</h3>
              <div>
                <Button
                  variant="outline"
                  onClick={() => { setShowIntakeForm(true); setIntakeProducts([]); setIntakePhone(""); }}
                  text="Deal Intake Form"
                />
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 sm:px-12 mt-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[13px] uppercase tracking-wider text-neutral-500">Stats</h2>
            <div className="flex items-center gap-2">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
                className="border border-neutral-300 rounded-xl px-4 py-2 text-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              >
                <option value="all">All Time</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
              </select>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard label="Mercury Balance" value={currency(mercuryBalance || 0)} />
            <StatCard label="Unpaid Commission" value={currency(unpaidCommission)} hint="Closed, not yet paid" />
            <StatCard label="Total Paid Commission" value={currency(totalPaidCommission)} />
            <StatCard label="Open Pipeline" value={currency(openPipelineValue)} hint="Annual value (Active pipeline)" />
            <StatCard label="Open Commission Value" value={currency(openCommissionValue)} hint="Annual value (Active pipeline)" />
            <StatCard label="Total Deals" value={`${filteredDeals.length.toLocaleString()}`} />
          </div>
        </div>
      </section>

      {/* Deal Modal */}
      {editing && (
        <DealModal
          deal={editing}
          partnerId={partnerId}
          onClose={() => setEditing(null)}
          onSaved={() => loadDeals(partnerId)}
        />
      )}

      {/* Deal Intake Form Modal */}
      {showIntakeForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-full max-w-2xl bg-white rounded-2xl border border-neutral-200 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Submit New Deal</h3>
              <Button variant="outline" size="sm" onClick={() => { setShowIntakeForm(false); setIntakeProducts([]); setIntakePhone(""); }} text="Close" />
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setSubmitting(true);
                const formData = new FormData(e.currentTarget);
                
                const newDeal = {
                  name: formData.get("dealName") as string,
                  company: formData.get("company") as string,
                  website: (formData.get("website") as string) || undefined,
                  contactName: formData.get("contactName") as string,
                  email: (formData.get("email") as string) || undefined,
                  phone: intakePhone || undefined,
                  dealValue: Number(formData.get("dealValue")),
                  status: formData.get("status") as string,
                  billingCycle: (formData.get("billingCycle") as string) || undefined,
                  notes: (formData.get("notes") as string) || undefined,
                  // New fields
                  closeProbability: Number(formData.get("closeProbability")),
                  expectedCloseDate: formData.get("expectedCloseDate") as string || undefined,
                  products: intakeProducts,
                };

                try {
                  const response = await fetch(`${API_URL}/deals/intake`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...newDeal, partnerId }),
                  });

                  if (response.ok) {
                    showToast("Deal submitted successfully!", "success");
                    setShowIntakeForm(false);
                    setIntakeProducts([]);
                    loadDeals(partnerId);
                  } else {
                    const error = await response.json();
                    showToast(`Failed to submit deal: ${error.error}`, "error");
                  }
                } catch (error) {
                  console.error("Failed to submit deal:", error);
                  showToast("Failed to submit deal.", "error");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Deal Name *</span>
                    <input type="text" name="dealName" className="border rounded-xl px-3 py-2" required />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Company *</span>
                    <input type="text" name="company" className="border rounded-xl px-3 py-2" required />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Deal Value *</span>
                    <input type="number" name="dealValue" className="border rounded-xl px-3 py-2" required />
                  </label>
                   <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Billing Cycle</span>
                    <select name="billingCycle" className="border rounded-xl px-3 py-2">
                      <option value="">Select cycle</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                      <option value="one_time">One Time</option>
                    </select>
                  </label>
                </div>

                {/* Forecasting Fields in Intake */}
                <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-3 rounded-xl border border-neutral-100">
                  <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Probability (%)</span>
                    <input type="number" name="closeProbability" min="0" max="100" className="border rounded-xl px-3 py-2" placeholder="50" />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Expected Close</span>
                    <input type="date" name="expectedCloseDate" className="border rounded-xl px-3 py-2" />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Contact</span>
                    <input type="text" name="contactName" className="border rounded-xl px-3 py-2" required />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Email</span>
                    <input type="email" name="email" className="border rounded-xl px-3 py-2" />
                  </label>
                </div>

                <label className="flex flex-col gap-2">
                    <span className="text-neutral-600">Phone</span>
                    <PhoneInput value={intakePhone} onChange={(val) => setIntakePhone(val || "")} className="rounded-xl" />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="text-neutral-600">Status</span>
                  <select name="status" className="border rounded-xl px-3 py-2" required>
                    <option value="qualified">Qualified</option>
                    <option value="pre-negotiation">In Talks (Pre-Negotiation)</option>
                    <option value="negotiation">In Talks (Negotiation)</option>
                    <option value="closed">Closed</option>
                    <option value="paid">Paid</option>
                  </select>
                </label>

                {/* Products Section */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center justify-between">
                     <h4 className="font-semibold text-neutral-900">Products</h4>
                     <select 
                       className="text-xs border rounded-lg px-2 py-1 max-w-[150px]"
                       onChange={(e) => {
                         const [prodId, priceIdx] = e.target.value.split('|');
                         if (prodId) {
                           const product = products.find(p => p.id === prodId);
                           if (product) {
                             const priceInfo = product.prices && product.prices[Number(priceIdx)];
                             const newDealProduct: DealProduct = {
                               dealId: "temp", 
                               productId: product.id,
                               quantity: 1,
                               priceAtDeal: priceInfo ? priceInfo.price : product.price,
                               product: product
                             };
                             setIntakeProducts([...intakeProducts, newDealProduct]);
                           }
                           e.target.value = "";
                         }
                       }}
                     >
                       <option value="">+ Add Product</option>
                       {products.map(p => {
                         if (p.prices && p.prices.length > 0) {
                           return p.prices.map((price, idx) => (
                             <option key={`${p.id}-${idx}`} value={`${p.id}|${idx}`}>
                               {p.name} - {price.label || price.recurringInterval} ({currency(price.price)})
                             </option>
                           ));
                         }
                         return (
                           <option key={p.id} value={`${p.id}|-1`}>{p.name} ({currency(p.price)})</option>
                         );
                       })}
                     </select>
                  </div>
                  
                  {intakeProducts.length === 0 ? (
                    <div className="text-neutral-400 text-center py-2 italic">No products added</div>
                  ) : (
                    <div className="space-y-2">
                      {intakeProducts.map((dp, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-neutral-50 p-2 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium">{dp.product?.name || "Unknown Product"}</div>
                            <div className="text-xs text-neutral-500">{dp.product?.recurringInterval}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-neutral-500">Qty</label>
                            <input 
                              type="number" 
                              min="1"
                              className="w-16 border rounded px-2 py-1 text-sm"
                              value={dp.quantity}
                              onChange={(e) => {
                                 const newProds = [...intakeProducts];
                                 newProds[idx].quantity = Number(e.target.value);
                                 setIntakeProducts(newProds);
                              }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-neutral-500">Price</label>
                            <input 
                              type="number" 
                              className="w-20 border rounded px-2 py-1 text-sm"
                              value={dp.priceAtDeal}
                              onChange={(e) => {
                                 const newProds = [...intakeProducts];
                                 newProds[idx].priceAtDeal = Number(e.target.value);
                                 setIntakeProducts(newProds);
                              }}
                            />
                          </div>
                          <Button 
                            variant="ghost"
                            type="button"
                            onClick={() => {
                               const newProds = [...intakeProducts];
                               newProds.splice(idx, 1);
                               setIntakeProducts(newProds);
                            }}
                            className="text-red-500 hover:bg-red-50 hover:text-red-700 p-1 h-6 w-6 rounded flex items-center justify-center min-h-0"
                            text="×"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-end gap-2 border-t pt-4">
                  <Button variant="outline" type="button" onClick={() => { setShowIntakeForm(false); setIntakeProducts([]); setIntakePhone(""); }} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit Deal"}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <section className="px-6 sm:px-12 mt-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[13px] uppercase tracking-wider text-neutral-500">Deal Pipeline</h2>
            <div className="text-[13px] text-neutral-500">Table view - Grouped by status</div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-neutral-200">
            <table className="w-full text-[13px]">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-4 py-2 w-[32ch]">
                    <SortHeader label="Deal Name" sortKey="name" sort={sort} setSort={setSort} />
                  </th>
                  <th className="text-left px-2 py-2 w-[12ch]">
                    <SortHeader label="Status" sortKey="status" sort={sort} setSort={setSort} />
                  </th>
                  <th className="text-center px-2 py-2 w-[14ch]">
                    <SortHeader label="Annual Value" sortKey="dealValue" sort={sort} setSort={setSort} align="center" />
                  </th>
                  <th className="text-center px-4 py-2 w-[16ch]">
                    <SortHeader label="Annual Commission" sortKey="commissionValue" sort={sort} setSort={setSort} align="center" />
                  </th>
                  <th className="text-left px-4 py-2 w-[12ch]">
                    <SortHeader label="Date" sortKey="createdAt" sort={sort} setSort={setSort} />
                  </th>
                  <th className="text-right px-4 py-2 w-[10ch]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(["qualified", "pre-negotiation", "negotiation", "closed", "paid", "lost"] as DealStatus[]).map((status) => (
                  groupedDeals[status]?.length > 0 && (
                    <div key={status} className="contents">
                      <tr className="bg-neutral-100">
                        <td colSpan={6} className="px-4 py-2 font-semibold text-[12px] uppercase tracking-wider text-neutral-600">
                          {status} ({groupedDeals[status].length})
                        </td>
                      </tr>
                      {groupedDeals[status].map((d) => (
                          <tr key={d.id} className="border-b border-neutral-200 hover:bg-neutral-50">
                            <td className="px-4 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                {d.name}
                                {isStale(d) && (
                                  <span title="Stale: No activity in 30+ days" className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-600 text-[10px] font-bold cursor-help">
                                    !
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-2"><StatusPill s={d.status} /></td>
                          <td className="px-2 py-2 text-center tabular-nums">
                            {currency(getAnnualValue(d.dealValue, d.billingCycle))}
                            {d.billingCycle && d.billingCycle !== 'annually' && (
                              <div className="text-[10px] text-neutral-400 font-normal">
                                {currency(d.dealValue)} / {d.billingCycle === 'monthly' ? 'mo' : 'qtr'}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center tabular-nums font-medium">
                            {currency(getAnnualValue(d.commissionValue, d.billingCycle))}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-neutral-600">{new Date(d.createdAt).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="outline"
                                size="sm"
                                className="h-auto min-h-0 py-1.5"
                                onClick={() => openDeal(d.id)}
                                disabled={openingId === d.id}
                                text={openingId === d.id ? "Loading..." : "Open"}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                className="h-auto min-h-0 py-1.5"
                                onClick={async () => {
                                  if (confirm("Are you sure you want to delete this deal?")) {
                                    try {
                                      setDeletingId(d.id);
                                      const response = await fetch(`${API_URL}/deals/${d.id}`, {
                                        method: "DELETE",
                                      });
                                      if (response.ok) {
                                        loadDeals(partnerId);
                                        showToast("Deal deleted", "success");
                                      } else {
                                        showToast("Failed to delete deal", "error");
                                      }
                                    } catch (error) {
                                      console.error("Failed to delete deal:", error);
                                      showToast("Failed to delete deal", "error");
                                    } finally {
                                      setDeletingId(null);
                                    }
                                  }
                                }}
                                disabled={deletingId === d.id}
                                text={deletingId === d.id ? "..." : "Delete"}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </div>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}

